import React, { useEffect, useState } from "react";
import { Dialog, Box, Typography, IconButton, TextField, Button, CircularProgress, Snackbar, Alert } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { IOrderListDTO, IOrderPickingDetailsDTO, IMarkItemPickedResponseDTO } from "../../interfaces/Orders";
import { getPickOrderDetails, pickorder, scanBarCode } from "../../services/Orders";

interface PickItemsDialogProps {
  open: boolean;
  onClose: () => void;
  onSave:() => void;
  order: IOrderListDTO | null;
}

const PickItemsDialog = ({ open, onClose, order,onSave }: PickItemsDialogProps)=> {
  const [skuInput, setSKUInput] = useState("");
  const [scanError, setScanError] = useState("");
  const [pickingDetails, setPickingDetails] = useState<IOrderPickingDetailsDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [msg, setMsg] = useState("");
  const [openError, setOpenError] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);

  const handleBarcodeScan = async () => {
    if (!skuInput.trim() || !order?.orderId) return;
    setScanError("");
    setScanning(true);
    try {
      const response = await scanBarCode(order.orderId, { barcode: skuInput.trim() });
      if (response?.status === "success" && response?.rawResponse?.data) {
        const res: IMarkItemPickedResponseDTO = response.rawResponse.data;
        if (res.picked && pickingDetails) {
          setPickingDetails({
            ...pickingDetails,
            itemsByBuilding: pickingDetails.itemsByBuilding.map((building) => ({
              ...building,
              items: building.items.map((item) =>
                item.inventoryId === res.inventoryId ? { ...item, picked: true } : item
              ),
            })),
          });
        }
        setSKUInput("");
      } else {
        setScanError(response?.rawResponse?.data?.message || "Item not found or already picked.");
      }
    } catch {
      setScanError("Error scanning barcode.");
    } finally {
      setScanning(false);
    }
  };

  useEffect(() => {
    if (!open || !order?.orderId) return;

    (async () => {
      try {
        setLoading(true);
        const res: IOrderPickingDetailsDTO = await getPickOrderDetails(order.orderId);
        if (res) {
          setPickingDetails(res);
        }
      } catch {
        setPickingDetails(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, order?.orderId]);

  const allItemsPicked = pickingDetails?.itemsByBuilding?.every(
    (building) => building.items.every((item) => item.picked)
  ) ?? false;
  const handleSave = async () => {
    if (!order) return;
  
    setLoading(true);
    try {
      const res = await pickorder(order.orderId);
      if (res && res.status === "success") {
        setOpenSuccess(true);
        setMsg("Order Picked Successfully.");
        onSave();
        onClose();
      } else {
        setOpenError(true);
        setMsg("Failed to pick Order.");
      }
    } catch (error) {
      setOpenError(true);
      setMsg("Unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };
  const handleClose = () => {
    if (openSuccess) {
      setOpenSuccess(false);
    } else if (openError) {
      setOpenError(false);
    }
    setMsg("");
  };
  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          position: "fixed",
          right: 0,
          top: 0,
          bottom: 0,
          m: 0,
          width: "450px",
          maxHeight: "100vh",
          borderRadius: 0,
        }
      }}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }
      }}
    >
      {/* Header - Fixed */}
      <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: "16px" }}>Pick Items</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>Pick items to add Package for delivering to the Packing.</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Scrollable Content */}
      <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: "12px", p:2 }}>Order Details</Typography>
      <Box sx={{ flex: 1, p:1,overflowY: "auto", "&::-webkit-scrollbar": { display: "none" }, msOverflowStyle: "none", scrollbarWidth: "none" }}>
        {/* Order Details */}
        <Box sx={{ p: 2, mb: 2, backgroundColor: "background.default", border: "1px solid", borderColor: "grey.light", borderRadius: "8px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "10px" }}>Order ID</Typography>
              <Typography variant="body2" fontWeight={500} sx={{ fontSize: "12px" }}>{pickingDetails?.orderId ?? order?.orderId}</Typography>
            </Box>
            <Box sx={{ height: "25px", width: "1px", backgroundColor: "grey.light" }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "10px" }}>Items</Typography>
              <Typography variant="body2" fontWeight={500} sx={{ fontSize: "12px" }}>{pickingDetails?.totalItems ?? order?.totalItems}</Typography>
            </Box>
            <Box sx={{ height: "25px", width: "1px", backgroundColor: "grey.light" }} />
            {/* <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "10px" }}>Type</Typography>
              <Typography variant="body2" fontWeight={500} sx={{ fontSize: "12px" }}>Outbound</Typography>
            </Box> */}
            <Box sx={{ height: "25px", width: "1px", backgroundColor: "grey.light" }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "10px" }}>Packing Building</Typography>
              <Typography variant="body2" fontWeight={500} sx={{ fontSize: "12px" }}>{pickingDetails ? `${pickingDetails.packingBuildingName} - ${pickingDetails.packingFloorName} ${pickingDetails.packingRoomName}` : ''}</Typography>
            </Box>
          </Box>
        </Box>

        {/* Enter SKU */}
        <TextField
          fullWidth
          size="small"
          placeholder="Enter Barcode or Scan"
          value={skuInput}
          onChange={(e) => { setSKUInput(e.target.value); setScanError(""); }}
          onKeyDown={(e) => { if (e.key === "Enter") handleBarcodeScan(); }}
          disabled={scanning}
          error={!!scanError}
          helperText={scanError}
          sx={{
            mb: 2,
            backgroundColor: "#F9FAFB",
            "& .MuiOutlinedInput-root": {
              borderRadius: "6px",
              "& fieldset": { borderColor: scanError ? "error.main" : "rgba(74,85,101,0.15)" },
              "&:hover fieldset": { borderColor: "#000" },
              "&.Mui-focused fieldset": { borderColor: "#000" },
            },
            "& .MuiInputBase-input": { fontSize: "12px", padding: "10px 12px" },
          }}
        />

        {/* Smart Route Plan */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <AltRouteIcon sx={{ fontSize: "18px", color: "text.primary" }} />
          <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: "13px" }}>Smart Route Plan</Typography>
        </Box>

        {/* Buildings with Custom Stepper */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
        <Box>
          {pickingDetails?.itemsByBuilding?.map((building, index) => (
            <Box key={building.buildingId} sx={{ display: "flex", gap: 2, pb: index < (pickingDetails.itemsByBuilding.length - 1) ? 3 : 0 }}>
              {/* Timeline */}
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative", width: 24 }}>
                <Box sx={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: "secondary.main", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}>
                  <Typography sx={{ color: "white", fontSize: "11px", fontWeight: 600 }}>{index + 1}</Typography>
                </Box>
                {index < (pickingDetails.itemsByBuilding.length - 1) && (
                  <Box sx={{ width: 2, position: "absolute", top: 24, bottom: -24, backgroundColor: "grey.light", left: "50%", transform: "translateX(-50%)" }} />
                )}
              </Box>

              {/* Content */}
              <Box sx={{ flex: 1, pb: 1 }}>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: "13px" }}>{building.buildingName}</Typography>
                <Typography variant="caption" sx={{ fontSize: "11px", color: "primary.main", mb: 1, display: "block" }}>{building.itemsToPickCount} Item to Pick</Typography>

                {/* Items List */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {building.items.map((item, itemIndex) => (
                  <Box
                    key={`${building.buildingId}-${item.inventoryId}-${itemIndex}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: "8px",
                      backgroundColor: item.picked ? "success.light" : "#F9FAFB",
                      border: "1px solid",
                      borderColor: item.picked ? "rgba(0, 178, 33, 0.2)" : "rgba(74,85,101,0.15)",
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "6px",
                        backgroundColor: "#E5E7EB",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Box component="img" src={item.imageUrl || "/placeholder.png"} alt={item.itemName} sx={{ width: 30, height: 30, objectFit: "contain" }} onError={(e: any) => { e.target.style.display = "none"; }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: "12px" }}>{item.itemName}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "10px" }}>
                        Floor: {item.floorName} · Room: {item.roomName} · Rack: {item.rackName} · Bin: {item.binName} · Barcode: {item.barcodeLast4Digits}
                      </Typography>
                    </Box>
                    {item.picked ? (
                      <CheckCircleIcon sx={{ color: "success.main", fontSize: "20px" }} />
                    ) : (
                      <RadioButtonUncheckedIcon sx={{ color: "grey.primary", fontSize: "20px" }} />
                    )}
                  </Box>
                ))}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
        )}
      </Box>

      {/* Footer - Fixed */}
      <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", borderTop: "1px solid", borderColor: "grey.300", backgroundColor: "#fff" }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "primary.main",
            boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset",
            padding: "8px 16px",
            textTransform: "none",
            fontSize: "13px",
            "&:hover": {
              border: "1px solid",
              borderColor: "primary.main",
              boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset"
            }
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          disabled={!allItemsPicked || loading}
          onClick={handleSave}
          sx={{
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "primary.main",
            boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset",
            padding: "8px 24px",
            textTransform: "none",
            fontSize: "13px",
            "&:hover": {
              backgroundColor: "primary.main",
              boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset"
            }
          }}
        >
          Save
        </Button>
      </Box>
    </Dialog>
     {/** Snackbar alerts */}
     <Snackbar
     anchorOrigin={{ vertical: "top", horizontal: "right" }}
     open={openError}
     autoHideDuration={5000}
     onClose={handleClose}
   >
     <Alert severity="error" variant="filled" sx={{ width: "100%" }}>
       {msg}
     </Alert>
   </Snackbar>

   <Snackbar
     anchorOrigin={{ vertical: "top", horizontal: "right" }}
     open={openSuccess}
     autoHideDuration={5000}
     onClose={handleClose}
   >
     <Alert severity="success" variant="filled" sx={{ width: "100%" }}>
       {msg}
     </Alert>
   </Snackbar>
   </>
  );
}
export default PickItemsDialog;
